// Microsoft Clarity: production-only, consent-gated, never records private pages.
export const CLARITY_PROJECT_ID = 'z61r7iwzg7';
const PRODUCTION_HOSTS = ['rcmbiosciences.com', 'www.rcmbiosciences.com'];
export const CONSENT_KEY = 'rcm_analytics_consent';
export const CONSENT_EVENT = 'rcm-consent-change';
export const OPEN_CONSENT_EVENT = 'rcm-open-consent';
const DEBUG_KEY = 'rcm_clarity_debug';

const EXCLUDED_PATHS = [
  /^\/admin/, /^\/checkout/, /^\/order-history/, /^\/wishlist/, /^\/settings/,
  /^\/partner-dashboard/, /^\/confirm\//, /^\/reset-password/, /^\/invoice-lookup/,
  /^\/track-order/, /^\/unsubscribe/, /^\/messages/, /^\/chat\//, /^\/community/,
];

const state = { loaded: false, recording: false };

export const isExcludedPath = (pathname) => EXCLUDED_PATHS.some((re) => re.test(pathname));

// 'live' on the production domain, 'debug' (no network, calls logged) in Preview with ?clarity_debug=1, else 'off'.
export const getClarityMode = () => {
  if (typeof window === 'undefined') return 'off';
  if (PRODUCTION_HOSTS.includes(window.location.hostname)) return 'live';
  try {
    const flag = new URLSearchParams(window.location.search).get('clarity_debug');
    if (flag === '1') sessionStorage.setItem(DEBUG_KEY, '1');
    if (flag === '0') sessionStorage.removeItem(DEBUG_KEY);
    return sessionStorage.getItem(DEBUG_KEY) === '1' ? 'debug' : 'off';
  } catch {
    return 'off';
  }
};

export const getConsent = () => {
  try { return localStorage.getItem(CONSENT_KEY); } catch { return null; }
};

export const setConsent = (value) => {
  try { localStorage.setItem(CONSENT_KEY, value); } catch {}
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
};

const call = (...args) => { if (typeof window.clarity === 'function') window.clarity(...args); };

const loadClarity = (mode) => {
  if (state.loaded || window.__rcmClarityLoaded) return;
  state.loaded = true;
  window.__rcmClarityLoaded = true;
  if (mode === 'debug') {
    window.__clarityLog = [];
    window.clarity = (...args) => window.__clarityLog.push(args);
  } else {
    window.clarity = window.clarity || function clarityQueue() {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };
    const tag = document.createElement('script');
    tag.async = true;
    tag.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
    tag.setAttribute('data-testid', 'clarity-script');
    document.head.appendChild(tag);
  }
  call('consentv2', { ad_Storage: 'denied', analytics_Storage: 'granted' });
  call('set', 'environment', mode === 'live' ? 'production' : 'preview-test');
};

const pageType = (p) => {
  if (p === '/') return 'home';
  if (p === '/products') return 'catalog';
  if (p.startsWith('/products/') || p.startsWith('/product/')) return 'product';
  if (p.startsWith('/coa')) return 'coa';
  if (p.startsWith('/blog')) return 'blog';
  return 'content';
};

export const syncClarity = (pathname) => {
  const mode = getClarityMode();
  if (mode === 'off') return;
  if (getConsent() !== 'granted') {
    if (state.loaded) {
      call('consentv2', { ad_Storage: 'denied', analytics_Storage: 'denied' });
      if (state.recording) call('stop');
    }
    state.recording = false;
    return;
  }
  if (isExcludedPath(pathname)) {
    if (state.recording) call('stop');
    state.recording = false;
    return;
  }
  if (!state.loaded) loadClarity(mode);
  else if (!state.recording) {
    call('consentv2', { ad_Storage: 'denied', analytics_Storage: 'granted' });
    call('start');
  }
  state.recording = true;
  call('set', 'page_type', pageType(pathname));
};

// Non-personal events only: product codes, categories and counts — never names, emails or search text.
export const trackClarityEvent = (name, tags = {}) => {
  if (!state.recording) return;
  Object.entries(tags).forEach(([k, v]) => call('set', k, String(v)));
  call('event', name);
};
